import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabaseClient'; // Your Supabase client
import {
  Container, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert,
  Button,
  Paper
} from '@mui/material';

// Shared function to call the backend
// Consider moving this to a shared api.js or utils.js file in your frontend src
async function callProcessInvitationAPI(token, userAccessToken) {
  console.log('[Frontend] Calling /functions/v1/process-invitation with token:', token);
  try {
    const response = await fetch('/functions/v1/process-invitation', { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userAccessToken}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlhdCI6MTc0NzEyNjU3NSwiZXhwIjoyMDYyNDg2NTc1LCJpc3MiOiJzdXBhYmFzZSJ9.oIo6GvVIQ0CjeO9vBepnZUNvB9RqzYbQXlE8nIJWVOg'
      },
      body: JSON.stringify({ invitation_token: token })
    });

    const responseBody = await response.json(); 
    if (!response.ok) {
      console.error('[Frontend] Backend /process-invitation error:', responseBody);
      throw new Error(responseBody.details || responseBody.error || 'Failed to process invitation via backend.');
    }
    console.log('[Frontend] Backend /process-invitation success:', responseBody);
    return { success: true, data: responseBody };
  } catch (error) {
    console.error('[Frontend] Exception calling /process-invitation:', error);
    return { success: false, error: error.message || 'Network error or failed to parse response.' };
  }
}

function AcceptInvitationPage() {
  console.log('[AcceptInvitationPage] COMPONENT RENDERING - START');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  console.log('[AcceptInvitationPage] searchParams object:', searchParams);
  console.log('[AcceptInvitationPage] navigate function:', navigate);
  
  const [error, setError] = useState('');
  const [message, setMessage] = useState('Preparing your invitation...');
  const [isLoading, setIsLoading] = useState(true);
  
  console.log('[AcceptInvitationPage] COMPONENT RENDERING - HOOKS INITIALIZED');

  useEffect(() => {
    let timeoutId;
    
    try {
      console.log('[AcceptInvitationPage] Component mounted, checking URL params...');
      console.log('[AcceptInvitationPage] Full URL:', window.location.href);
      console.log('[AcceptInvitationPage] Search params:', window.location.search);
      
      const tokenFromUrl = searchParams.get('invitation_token');
      const teamIdFromUrl = searchParams.get('team_id');
      console.log('[AcceptInvitationPage] Token from URL:', tokenFromUrl);
      console.log('[AcceptInvitationPage] Team ID from URL:', teamIdFromUrl);

      if (!tokenFromUrl) {
        console.log('[AcceptInvitationPage] No token found, showing error');
        setError('Invalid or missing invitation token in the link. Please use the link provided in your email.');
        setMessage('');
        setIsLoading(false);
        return;
      }

      console.log('[AcceptInvitationPage] Token found, clearing old data and storing new token');
      try {
        // Clear any old invitation data first
        localStorage.removeItem('pendingInvitationToken');
        localStorage.removeItem('isInviteeSignup');
        
        // Store new invitation data
        localStorage.setItem('pendingInvitationToken', tokenFromUrl);
        localStorage.setItem('isInviteeSignup', 'true');
        console.log('[AcceptInvitationPage] Successfully stored new invitation token in localStorage');
      } catch (storageError) {
        console.error('[AcceptInvitationPage] Error storing in localStorage:', storageError);
      }
      
      const invitedEmailFromUrl = searchParams.get('email'); 
      console.log('[AcceptInvitationPage] Invited email from URL:', invitedEmailFromUrl);
      
      console.log('[AcceptInvitationPage] About to navigate to /signup');
      setMessage('Redirecting to sign-up to accept your invitation...');
      setIsLoading(false);
      
      timeoutId = setTimeout(() => {
        try {
          console.log('[AcceptInvitationPage] Executing navigation to /signup');
          navigate('/signup', { 
            replace: true, 
            state: { 
              invitationToken: tokenFromUrl, 
              invitedEmail: invitedEmailFromUrl
            }
          });
        } catch (navError) {
          console.error('[AcceptInvitationPage] Navigation error:', navError);
          setError('Error navigating to signup page. Please try again.');
          setIsLoading(false);
        }
      }, 2000);
      
    } catch (error) {
      console.error('[AcceptInvitationPage] Unexpected error in useEffect:', error);
      setError('An unexpected error occurred. Please try the invitation link again.');
      setIsLoading(false);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Accepting Invitation
        </Typography>
        {(isLoading || message) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {message || 'Processing your invitation link...'}
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2, mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
}

export default AcceptInvitationPage; 